/**
 * The call filters that are not saved views.
 *
 * The rail already owns everything derived from status and the two attachment
 * flags (`CALL_FILTERS`), so this file deliberately does NOT offer a transcript
 * filter: there is a "Transcribed" folder two inches to the left, and a second
 * control that sets the same query is a control that can disagree with it.
 *
 * What is left is the pair the rail cannot express - where the number was picked
 * up, and how long the call actually ran.
 */
import type { CallListParams, CallSource } from "../../../lib/calls";

/**
 * What the source filter can pick: a place a person clicked a number, or the AI
 * dialling on its own. "inbound" is left out on purpose - the rail's direction
 * folders already answer that one.
 */
export type CallFilterSource = CallSource | "ai-outbound";

export interface CallFilters {
  source: CallFilterSource | "all";
  /**
   * Talk-time floor in seconds. 0 means no floor.
   *
   * A floor above zero drops every unanswered call, because an unanswered call
   * has no duration at all. That is the right reading of "calls longer than a
   * minute", but it does mean this and the Missed folder cancel each other out.
   */
  minSec: number;
  /**
   * One operator label id, or null for "any label".
   *
   * Set from the rail, kept here: this object is what decides whether the list
   * is still the whole log, and a label kept outside it would leave the filter
   * dot dark and the empty state blaming the folder for a label with no calls.
   */
  tag: string | null;
}

export const DEFAULT_CALL_FILTERS: CallFilters = { source: "all", minSec: 0, tag: null };

/** Message-key suffix plus the seconds it stands for. Three steps, because the
 *  question behind them is only ever "did we actually talk". */
export const DURATION_STEPS: { key: string; sec: number }[] = [
  { key: "any", sec: 0 },
  { key: "min1", sec: 60 },
  { key: "min5", sec: 300 },
];

export const CALL_SOURCES: CallFilterSource[] = ["manual", "ariregister", "scanner", "scan-leads", "ai-outbound"];

export function callFiltersActive(f: CallFilters): boolean {
  return f.source !== "all" || f.minSec > 0 || f.tag !== null;
}

/** Only the keys worth sending: "all", a zero floor and a null label are the
 *  absence of a filter, not a filter for those values. `null` in particular
 *  would serialize as the string "null" and match nothing at all. */
export function callFilterQuery(f: CallFilters): Pick<CallListParams, "source" | "minSec" | "tag"> {
  return {
    source: f.source === "all" ? undefined : f.source,
    minSec: f.minSec > 0 ? f.minSec : undefined,
    tag: f.tag ?? undefined,
  };
}
